import { useMemo, useState, FC } from "react";
// Utils
import cn from "classnames";
import { getAnimalInfoItems } from "./helpers";
import { getFieldName } from "helpers/myLivestock";
// Components
import ConfirmMoveToGroupModal from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMoveToGroupModal";
import ConfirmMoveToFieldModal from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMoveToFieldModal";
import { Button, Text, Flex, KeyValuePair, Pill, Spacer } from "components";
import { Tag } from "tags/components/Tag";
import { useFormatDate, useGetCurrentBusinessUnit } from "hooks";
import { GQLAnimal } from "pages/MyLivestock/Animals/AnimalView/AnimalView";
import { ActiveIdentifierPreferencesType } from "generated/graphql";
import styles from "./AnimalInfoBlock.module.scss";
import { Terms } from "helpers/translations/src/useLocale/types";
import { useLocale } from "helpers/translations/src";

export interface AnimalInfoBlockProps {
  animal: GQLAnimal;
  isArchived: boolean;
  canAddToGroup: boolean;
  activeIdPreferences: ActiveIdentifierPreferencesType | null | undefined;
  terms: Terms;
}

export const AnimalInfoBlock: FC<AnimalInfoBlockProps> = ({
  animal,
  isArchived = false,
  canAddToGroup,
  activeIdPreferences,
}) => {
  const [showGroupModal, setGroupModalShow] = useState(false);
  const [showFieldModal, setFieldModalShow] = useState(false);
  const { getFormatDate } = useFormatDate();
  const { countryCode } = useGetCurrentBusinessUnit();
  const { terms } = useLocale();
  const animalInfo = useMemo(
    () => getAnimalInfoItems(animal, isArchived, getFormatDate, terms, activeIdPreferences, countryCode),
    [activeIdPreferences, animal, countryCode, getFormatDate, isArchived, terms],
  );

  const AnimalTags = ({ tags }) => {
    return tags.map((tag) => {
      const t = {
        tagId: tag.tagId,
        name: tag.name,
        color: tag.color,
        ownership: tag.schema.ownership,
      };

      return (
        <Flex key={tag.tagId} item>
          <Tag tag={t} />
        </Flex>
      );
    });
  };
  return (
    <>
      <Spacer baselineHeight={2} />
      <Flex container itemGrow containerWrap="wrap">
        <>
          <Flex
            container
            containerDirection="column"
            item
            itemGutter
            xs={12}
            m={animalInfo[0].filter((item) => item.value != null).length <= 5 ? 3 : 12}
            xl={animalInfo[0].filter((item) => item.value != null).length <= 5 ? 3 : 6}
          >
            <ul
              className={cn({
                [styles["animalInfo_details"]]: animalInfo[0].filter((item) => item.value != null).length >= 5,
              })}
            >
              {animalInfo[0].map((item) => {
                if (!item.isHidden) {
                  return (
                    <li key={item.label}>
                      <KeyValuePair label={item.label} unit={item.unit} value={item.value} canTruncate={true} />
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          </Flex>
          <Flex
            container
            containerDirection="column"
            item
            itemGutter
            xs={12}
            m={animalInfo[0].filter((item) => item.value != null).length <= 5 ? 3 : 12}
            xl={animalInfo[0].filter((item) => item.value != null).length <= 5 ? 3 : 6}
          >
            <ul className={styles.animalInfo_details}>
              {animalInfo[1].map((item) => {
                if (!item.isHidden && item.value != null) {
                  return (
                    <li key={item.label}>
                      <KeyValuePair label={item.label} unit={item.unit} value={item.value} />
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          </Flex>
        </>
      </Flex>

      <Spacer baselineHeight={2} />

      <Flex container>
        <Flex item itemGutter xs={12} m={6} xl={3}>
          <KeyValuePair
            label="Group"
            value={
              <Flex container>
                <Flex item>
                  <Pill
                    caption={animal?.group ? animal?.group.name : "No group"}
                    colour={animal?.group ? "yellow" : "grey"}
                    wrap
                  />
                </Flex>
                {!isArchived ? (
                  <Flex item itemGutter>
                    <Button
                      caption={animal?.group ? "change the group" : "add to group"}
                      variant="ghost"
                      onClick={(): void | false => canAddToGroup && setGroupModalShow(true)}
                    />
                  </Flex>
                ) : null}
              </Flex>
            }
            stacked
          />
        </Flex>
        <Flex item itemGutter xs={12} m={6} xl={3}>
          <KeyValuePair
            label={"location"}
            value={
              <Flex container>
                <Flex item>
                  <Pill
                    caption={
                      animal?.field
                        ? getFieldName({ fieldName: animal.field.name, unitName: animal.field.location?.unitName })
                        : "No location"
                    }
                    colour={animal?.field ? "yellow" : "grey"}
                    wrap
                  />
                </Flex>
                {!isArchived ? (
                  <Flex item itemGutter>
                    <Button caption="move" variant="ghost" onClick={(): void => setFieldModalShow(true)} />
                  </Flex>
                ) : null}
              </Flex>
            }
            stacked
          />
        </Flex>
        <Flex item itemGutter xs={12} m={6} xl={3}>
          <Text className={styles.tags_title}>Tags:</Text>
          <Flex container containerDirection="row" xs={12} m={6} xl={3}>
            <AnimalTags tags={animal?.tags} />
          </Flex>
        </Flex>
      </Flex>

      <Spacer baselineHeight={1} />

      {!isArchived && showGroupModal ? (
        <ConfirmMoveToGroupModal
          animals={[animal]}
          showModal={showGroupModal}
          onClose={(): void => setGroupModalShow(false)}
          isRefetchSingleAnimalInfo
        />
      ) : null}

      {!isArchived && showFieldModal ? (
        <ConfirmMoveToFieldModal
          animalPassportId={`${animal?.passportNumber} ${animal?.visualId ? `(${animal?.visualId})` : ""}`}
          animals={[animal?.id]}
          showModal={showFieldModal}
          isRefetchSingleAnimalInfo
          onClose={(): void => setFieldModalShow(false)}
        />
      ) : null}
    </>
  );
};
